import {
  Box,
  BlockStack,
  Card,
  InlineStack,
  Select,
  Text,
} from "@shopify/polaris";

import {
  canonicalStockyPublicBigIntId,
  isStockyPublicBigIntId,
  type StockyPublicBigIntId,
} from "../../api/stocky_public_ids";
import { t } from "../../lib/i18n";

type SelectOption = { label: string; value: string };

/**
 * 配送先の表示モード。
 * - select: 複数ロケーション店。ロケーションを選んで拠点別ドラフトを取り直す。
 * - fixed:  単一ロケーション店。配送先はその1拠点に確定（読み取り専用表示）。
 * - none:   ロケーション未同期など。配送先を出さない。
 */
export type DestinationProp =
  | {
      mode: "select";
      options: SelectOption[];
      value: string;
      onChange: (value: string) => void;
      disabled: boolean;
    }
  | { mode: "fixed"; name: string }
  | { mode: "none" };

type Props = {
  supplierOptions: SelectOption[];
  /** 選択中サプライヤーの supplier_id。対象なしのときは null。 */
  selectedSupplierId: StockyPublicBigIntId | null;
  onSupplierChange: (supplierId: StockyPublicBigIntId) => void;
  destination: DestinationProp;
};

/**
 * 純正エディタの「サプライヤー → 配送先」ヘッダ。左に自動生成明細が続く。
 * 単一ロケ店では配送先はその拠点に確定するため、セレクタではなく読み取り専用で表示する。
 *
 * 🔴 `supplierOptions` は登録済み仕入先ではなく**注文書ドラフト**から作られる
 * （呼び出し側が `drafts.map(...)` を渡す）。したがって発注が必要な商品が0件だと、
 * 仕入先が登録済みでもここは空になる。以前はその状態で「対象商品がありません」を
 * 無効セレクトに出していたが、**「サプライヤー」というラベルの欄がそう言うと
 * 「仕入先が登録されていない」と読まれる**（2026-07-30 に実際に起きた）。
 * 空である理由の説明は `SupplierSetupCard` が一手に持つ約束なので、ここは黙って
 * 引っ込む＝同じ空を2箇所が別の言葉で説明する状態を作らない。
 */
export function SupplierDestinationHeader({
  supplierOptions,
  selectedSupplierId,
  onSupplierChange,
  destination,
}: Props) {
  const hasSuppliers = supplierOptions.length > 0;

  // ドラフトが無く拠点も選べない＝出す情報がゼロ。カードごと消す
  // （配送先だけが残ると、何の配送先なのか宙に浮く）。
  if (!hasSuppliers && destination.mode !== "select") return null;

  return (
    <Card>
      <InlineStack gap="300" blockAlign="end" wrap={false}>
        {hasSuppliers && (
          <Box minWidth="240px">
            <Select
              label={t("サプライヤー")}
              options={supplierOptions}
              value={
                selectedSupplierId === null ? "" : String(selectedSupplierId)
              }
              onChange={(value) => {
                if (!isStockyPublicBigIntId(value)) return;
                onSupplierChange(canonicalStockyPublicBigIntId(value));
              }}
            />
          </Box>
        )}
        {hasSuppliers && destination.mode !== "none" && (
          <Box paddingBlockEnd="150">
            <Text as="span" variant="headingLg" tone="subdued">
              →
            </Text>
          </Box>
        )}
        {destination.mode === "select" && (
          <Box minWidth="240px">
            <Select
              label={t("配送先")}
              options={destination.options}
              value={destination.value}
              onChange={destination.onChange}
              disabled={destination.disabled}
            />
          </Box>
        )}
        {destination.mode === "fixed" && (
          <Box minWidth="240px" paddingBlockEnd="100">
            <BlockStack gap="050">
              <Text as="span" variant="bodyMd" tone="subdued">
                {t("配送先")}
              </Text>
              <Text as="span" variant="bodyMd" fontWeight="semibold">
                {destination.name}
              </Text>
            </BlockStack>
          </Box>
        )}
      </InlineStack>
    </Card>
  );
}
